/**
 * CrmWebhookSetupPanel — UI for setting up CRM webhook integration.
 * Shows webhook URL, API key info, event types, and CRM-specific instructions.
 * Data from any CRM connected via webhook appears in the main dashboard.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Loader2, Copy, Check, ChevronDown, ChevronUp, Activity } from "lucide-react";

// ─── Per-conversion payload templates ───────────────────

type ConversionTemplate = {
  labelKey: string;
  colorClass: string;
  payload: string;
};

const CRM_CONVERSION_TEMPLATES: ConversionTemplate[] = [
  {
    labelKey: "crmWebhook.template.lead",
    colorClass: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    payload: `{
  "event":        "lead",
  "email":        "jan@firma.pl",       // wymagane
  "phone":        "+48123456789",
  "first_name":   "Jan",
  "last_name":    "Kowalski",
  "utm_source":   "facebook",
  "utm_campaign": "kampania_lato",
  "utm_medium":   "cpc",
  "utm_content":  "ad_variant_A"
}`,
  },
  {
    labelKey: "crmWebhook.template.bookedCall",
    colorClass: "text-orange-400 bg-orange-500/10 border-orange-500/30",
    payload: `{
  "event":        "booked_call",
  "email":        "jan@firma.pl",       // wymagane
  "phone":        "+48123456789",
  "call_date":    "2024-04-15T10:00:00Z",
  "setter_name":  "Anna Kowalska",
  "lead_source":  "facebook_ads",
  "utm_source":   "facebook",
  "utm_campaign": "kampania_lato"
}`,
  },
  {
    labelKey: "crmWebhook.template.qualifiedCall",
    colorClass: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
    payload: `{
  "event":        "qualified_call",
  "email":        "jan@firma.pl",       // wymagane
  "phone":        "+48123456789",
  "call_date":    "2024-04-15T10:00:00Z",
  "closer_name":  "Piotr Nowak",
  "lead_score":   85,
  "utm_campaign": "kampania_lato"
}`,
  },
  {
    labelKey: "crmWebhook.template.purchase",
    colorClass: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    payload: `{
  "event":        "purchase",
  "email":        "jan@firma.pl",       // wymagane
  "phone":        "+48123456789",
  "order_id":     "ORD-12345",
  "revenue":      4997.00,
  "currency":     "PLN",
  "product_name": "Kurs Digital Marketing",
  "utm_source":   "facebook",
  "utm_campaign": "kampania_lato"
}`,
  },
  {
    labelKey: "crmWebhook.template.upsell",
    colorClass: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
    payload: `{
  "event":        "upsell",
  "email":        "jan@firma.pl",       // wymagane
  "order_id":     "ORD-12345-UP",
  "revenue":      1997.00,
  "currency":     "PLN",
  "product_name": "Mentoring Premium",
  "utm_campaign": "kampania_lato"
}`,
  },
];

function ConversionTemplatesSection() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const template = CRM_CONVERSION_TEMPLATES[active] as ConversionTemplate;

  return (
    <div className="p-3 rounded-lg border border-white/[0.08] bg-white/[0.02] space-y-3">
      <div className="flex items-center gap-2">
        <span className="bg-purple-500/20 text-purple-400 text-xs font-bold px-2 py-0.5 rounded">
          4
        </span>
        <span className="text-sm font-medium text-white">{t("crmWebhook.step4Title")}</span>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {CRM_CONVERSION_TEMPLATES.map((tpl, i) => (
          <button
            key={tpl.labelKey}
            onClick={() => setActive(i)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              i === active
                ? tpl.colorClass
                : "text-white/40 bg-white/[0.03] border-white/[0.08] hover:text-white/60"
            }`}
          >
            {t(tpl.labelKey)}
          </button>
        ))}
      </div>
      <div className="rounded-lg border border-white/[0.08] bg-black/20 overflow-hidden">
        <pre className="text-[11px] font-mono text-emerald-300/90 p-3 overflow-x-auto whitespace-pre-wrap leading-relaxed">
          {template.payload}
        </pre>
      </div>
    </div>
  );
}

interface WebhookSetupData {
  webhookUrl: string;
  authMethod: string;
  hasApiKey: boolean;
  apiKeys: Array<{
    id: string;
    name: string;
    prefix: string;
    createdAt: string;
    lastUsedAt: string | null;
  }>;
  eventTypes: Array<{ name: string; description: string; example: string }>;
  examplePayload: Record<string, unknown>;
  exampleCurl: string;
  crmInstructions: Array<{ crm: string; steps: string[] }>;
}

interface WebhookStats {
  totalEventsLast30d: number;
  byEventType: Array<{ eventName: string; count: number; lastEventAt: string }>;
}

const EVENT_COLORS: Record<string, string> = {
  lead: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  booked_call: "text-orange-400 bg-orange-500/10 border-orange-500/30",
  qualified_call: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
  purchase: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  upsell: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
  refund: "text-red-400 bg-red-500/10 border-red-500/30",
};

export default function CrmWebhookSetupPanel() {
  const { t, i18n } = useTranslation();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [expandedCrm, setExpandedCrm] = useState<string | null>(null);
  const [showPayload, setShowPayload] = useState(false);

  const { data: setup, isLoading } = useQuery<WebhookSetupData>({
    queryKey: ["crm-webhook-setup"],
    queryFn: () => apiRequest("GET", "/api/integrations/crm-webhook/setup"),
    staleTime: 60_000,
  });

  const { data: stats } = useQuery<WebhookStats>({
    queryKey: ["crm-webhook-stats"],
    queryFn: () => apiRequest("GET", "/api/integrations/crm-webhook/stats"),
    staleTime: 30_000,
  });

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4">
        <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
        <span className="text-sm text-white/60">{t("crmWebhook.loading")}</span>
      </div>
    );
  }

  if (!setup) return null;

  return (
    <div className="space-y-4 mt-3">
      {/* Stats summary */}
      {stats && stats.totalEventsLast30d > 0 && (
        <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-4 w-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">
              {t("crmWebhook.eventsLast30d", { count: stats.totalEventsLast30d })}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.byEventType.map((ev) => (
              <Badge
                key={ev.eventName}
                className={`text-xs border ${EVENT_COLORS[ev.eventName] || "text-white/60 bg-white/5 border-white/10"}`}
              >
                {ev.eventName}: {ev.count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Step 1: API Key */}
      <div className="p-3 rounded-lg border border-white/[0.08] bg-white/[0.02] space-y-2">
        <div className="flex items-center gap-2">
          <span className="bg-purple-500/20 text-purple-400 text-xs font-bold px-2 py-0.5 rounded">
            1
          </span>
          <span className="text-sm font-medium text-white">{t("crmWebhook.step1Title")}</span>
          {setup.hasApiKey ? (
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("crmWebhook.hasKey")}
            </Badge>
          ) : (
            <Badge className="text-xs bg-amber-600/20 text-amber-400 border-amber-500/50">
              {t("crmWebhook.createKey")}
            </Badge>
          )}
        </div>
        {setup.hasApiKey ? (
          <div className="space-y-1">
            {setup.apiKeys.map((k) => (
              <div key={k.id} className="flex items-center justify-between text-xs">
                <span className="text-white/70 font-mono">{k.prefix}</span>
                <span className="text-white/40">
                  {k.lastUsedAt
                    ? t("crmWebhook.lastUsed", {
                        date: new Date(k.lastUsedAt).toLocaleDateString(
                          i18n.language === "pl" ? "pl-PL" : "en-US"
                        ),
                      })
                    : t("crmWebhook.notUsed")}
                </span>
              </div>
            ))}
            <p className="text-xs text-white/40">{t("crmWebhook.useExisting")}</p>
          </div>
        ) : (
          <p className="text-xs text-white/50">{t("crmWebhook.createKeyHint")}</p>
        )}
      </div>

      {/* Step 2: Webhook URL */}
      <div className="p-3 rounded-lg border border-white/[0.08] bg-white/[0.02] space-y-2">
        <div className="flex items-center gap-2">
          <span className="bg-purple-500/20 text-purple-400 text-xs font-bold px-2 py-0.5 rounded">
            2
          </span>
          <span className="text-sm font-medium text-white">{t("crmWebhook.step2Title")}</span>
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs font-mono bg-black/30 text-purple-300 p-2 rounded border border-white/[0.06] truncate">
            {setup.webhookUrl}
          </code>
          <button
            onClick={() => copyToClipboard(setup.webhookUrl, "url")}
            className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            {copiedField === "url" ? (
              <Check className="h-4 w-4 text-green-400" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>
        <p className="text-xs text-white/40">{t("crmWebhook.urlHint")}</p>
      </div>

      {/* Step 3: Event types */}
      <div className="p-3 rounded-lg border border-white/[0.08] bg-white/[0.02] space-y-2">
        <div className="flex items-center gap-2">
          <span className="bg-purple-500/20 text-purple-400 text-xs font-bold px-2 py-0.5 rounded">
            3
          </span>
          <span className="text-sm font-medium text-white">{t("crmWebhook.step3Title")}</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-1.5">
          {setup.eventTypes.map((et) => (
            <div
              key={et.name}
              className={`p-2 rounded border text-xs ${EVENT_COLORS[et.name] || "text-white/60 bg-white/5 border-white/10"}`}
            >
              <div className="font-mono font-bold">{et.name}</div>
              <div className="text-white/50 mt-0.5">{et.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Step 4: Per-conversion payloads */}
      <ConversionTemplatesSection />

      {/* Example payload */}
      <div className="p-3 rounded-lg border border-white/[0.08] bg-white/[0.02] space-y-2">
        <button
          onClick={() => setShowPayload(!showPayload)}
          className="flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white w-full"
        >
          {showPayload ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {t("crmWebhook.payloadToggle")}
        </button>
        {showPayload && (
          <div className="space-y-2">
            <div className="relative">
              <pre className="text-xs font-mono bg-black/40 text-emerald-300 p-3 rounded border border-white/[0.06] overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(setup.examplePayload, null, 2)}
              </pre>
              <button
                onClick={() =>
                  copyToClipboard(JSON.stringify(setup.examplePayload, null, 2), "payload")
                }
                className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 text-white/40 hover:text-white"
              >
                {copiedField === "payload" ? (
                  <Check className="h-3.5 w-3.5 text-green-400" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
            <div className="relative">
              <pre className="text-xs font-mono bg-black/40 text-amber-300 p-3 rounded border border-white/[0.06] overflow-x-auto whitespace-pre-wrap">
                {setup.exampleCurl}
              </pre>
              <button
                onClick={() => copyToClipboard(setup.exampleCurl, "curl")}
                className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 text-white/40 hover:text-white"
              >
                {copiedField === "curl" ? (
                  <Check className="h-3.5 w-3.5 text-green-400" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CRM-specific instructions */}
      <div className="p-3 rounded-lg border border-white/[0.08] bg-white/[0.02] space-y-2">
        <div className="flex items-center gap-2">
          <span className="bg-purple-500/20 text-purple-400 text-xs font-bold px-2 py-0.5 rounded">
            5
          </span>
          <span className="text-sm font-medium text-white">{t("crmWebhook.step5Title")}</span>
        </div>
        <div className="space-y-1">
          {setup.crmInstructions.map((instr) => (
            <div key={instr.crm} className="rounded border border-white/[0.06]">
              <button
                onClick={() => setExpandedCrm(expandedCrm === instr.crm ? null : instr.crm)}
                className="w-full flex items-center justify-between p-2 text-xs font-medium text-white/80 hover:text-white hover:bg-white/[0.03]"
              >
                <span>{instr.crm}</span>
                {expandedCrm === instr.crm ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
              {expandedCrm === instr.crm && (
                <div className="px-3 pb-2">
                  <ol className="list-decimal list-inside space-y-1">
                    {instr.steps.map((step, i) => (
                      <li key={i} className="text-xs text-white/60">
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* What shows in dashboard */}
      <div className="p-3 rounded-lg border border-purple-500/20 bg-purple-500/5 text-xs text-white/60 space-y-1">
        <p className="font-medium text-purple-300">{t("crmWebhook.dashboardSummary.title")}</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>{t("crmWebhook.dashboardSummary.leads")}</li>
          <li>{t("crmWebhook.dashboardSummary.bookedCalls")}</li>
          <li>{t("crmWebhook.dashboardSummary.showRate")}</li>
          <li>{t("crmWebhook.dashboardSummary.salesRevenue")}</li>
          <li>{t("crmWebhook.dashboardSummary.closeRate")}</li>
          <li>{t("crmWebhook.dashboardSummary.roas")}</li>
        </ul>
      </div>
    </div>
  );
}
